<template>
  <base-rounded-button
    size="x-small"
    @click="getPdf"
    :tooltip="$i18n.t('default.generatePdf')"
    icon="mdi-printer"
    color="rgba(235, 235, 235, 0.5)"
    iconColor="#202124"
    :loading="loading"
  />
</template>
<script>
import { call, get } from 'vuex-pathify';
import { GeoJSON } from 'ol/format';
import { getCenter } from 'ol/extent';
import pdfCard from '@/mixins/pdfCard';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'FeatureCardPdfButton',
  mixins: [pdfCard, prepareAttributesFeatureCardMixin],
  props: {
    featureProperties: {
      type: Object,
      default: () => {
        return {};
      },
    },
    feature: {
      type: Object,
      default: () => {},
    },
    formSchema: {
      type: Array,
      required: true,
    },
    layer: {
      type: Object,
      required: true,
    },
    fileName: {
      type: String,
      default: '',
    },
  },
  computed: {
    users: get('users/users'),
    project: get('layers/project'),
    basemapLayer: get('layers/basemapLayer'),
    isBasemapLayerVisible() {
      return this.project?.basemap_visible;
    },
    descAttributeName() {
      return this.metadata[this.layer?.data_source_name]?.attributes_schema.desc_attribute_name;
    },
    isPrintingTwoModuleEnabled: get('admin/additionalModules@PRINTING_2_MODULE.enabled'),
    isPrintingTwoSettingsEnabled: get('admin/settingsValues@printing_2_module_enabled'),
    isPrintingTwoEnabled() {
      return this.isPrintingTwoModuleEnabled && this.isPrintingTwoSettingsEnabled;
    },
  },
  data: () => ({
    loading: false,
    logoUrl: `${process.env.VUE_APP_API_URL}/settings/logo`,
    printingLayersAttributes: ['labels_visible', 'layer_id', 'line_direction', 'opacity', 'style', 'type', 'visible'],
    currentTaskId: null,
    printParams: {
      paper_format: 'a4',
      scale: null,
      output_format: 'pdf',
      orientation: 'vertical',
      split: false,
      hide_footer: true,
      header_position: 'top',
      north_arrow: '',
      map_rotation: 0,
      show_legend: false,
      title: '',
      show_date: false,
      show_user: false,
      show_logo: false,
    },
  }),
  beforeDestroy() {
    if (this.currentTaskId) {
      this.stopCeleryTask(this.currentTaskId);
    }
  },
  methods: {
    mapPrint: call('map/mapPrint'),
    stopCeleryTask: call('celeryTasks/stopCeleryTask'),
    async getPdf() {
      this.loading = true;
      if (this.feature?.geometry) {
        const layers = [];
        for (const projectLayer of JSON.parse(JSON.stringify(this.$_getFlatGroupsLayers(this.project.layers).layers))) {
          for (const key of Object.keys(projectLayer)) {
            if (!this.printingLayersAttributes.includes(key)) {
              delete projectLayer[key];
            }
          }
          layers.push(projectLayer);
        }
        const olGeometry = new GeoJSON().readGeometry(this.feature.geometry, {
          featureProjection: this.$_config.defaultEpsg || 'EPSG:3857',
          dataProjection: this.$_config.defaultEpsg || 'EPSG:3857',
        });
        const center = getCenter(olGeometry.getExtent());
        const centerCoordsGeojson = {
          type: 'Point',
          crs: {
            type: 'name',
            properties: {
              name: this.$_config.defaultEpsg || 'EPSG:4326',
            },
          },
          coordinates: center,
        };
        this.printParams.scale = Math.max(500, this.calculatePrintScale(olGeometry.getExtent()));
        const payload = {
          ...this.printParams,
          layers,
          map_center: centerCoordsGeojson,
        };
        if (this.isBasemapLayerVisible) {
          payload.basemap = this.basemapLayer.role;
        }
        try {
          this.currentTaskId = await this.mapPrint({ payload, version: this.isPrintingTwoEnabled ? 'v2' : '' });
          this.$globalHub.$emit('handleCeleryTask', {
            currentTaskId: this.currentTaskId,
            taskStartSnackbar: {
              message: this.$i18n.t('snackbar.generatingPrint'),
            },
            taskFailureSnackbar: {
              message: this.$i18n.t('snackbar.generatingPrintError'),
            },
            taskFailureCallback: this.resetPdfTask,
            taskSuccessCallback: async r => {
              await this.getCardPdf(r);
              this.resetPdfTask();
            },
            taskSuccessResultParams: { download: false },
          });
        } catch (error) {
          this.resetPdfTask();
          console.log('error', error);
        }
      } else {
        this.getCardPdf();
      }
    },
    resetPdfTask() {
      this.currentTaskId = null;
      this.loading = false;
    },
    getCardPdf(mapPdf = null) {
      const featureUrl = `${process.env.VUE_APP_HOST}/map/layers/${this.layer.id}/${this.feature.id}`;
      const attributes = this.formSchema.map(gElement => {
        return {
          name: gElement.label,
          elements: gElement.elements.map(aElement => {
            return {
              name: aElement.unit ? `${aElement.label} [${aElement.unit}]` : aElement.label,
              value: this.getAttributeTextValue(this.featureProperties, aElement),
            };
          }),
        };
      });
      const lastChangeUserId = this.featureProperties.update_user;
      const lastChangeDate = this.featureProperties.update_datetime;
      const firstChangeUserId = this.featureProperties.create_user;
      const firstChangeDate = this.featureProperties.create_datetime;
      const config = {
        layerName: this.layer.name,
        featureId: this.featureProperties[this.descAttributeName],
        lastChangeDate: lastChangeDate ? this.$dayjs(lastChangeDate).format('L LT') : '',
        lastChangeUserName: lastChangeUserId ? this.users[lastChangeUserId]?.name : null,
        firstChangeDate: firstChangeDate ? this.$dayjs(firstChangeDate).format('L LT') : '',
        firstChangeUserName: firstChangeUserId ? this.users[firstChangeUserId]?.name : null,
        logoUrl: this.logoUrl,
        featureUrl,
        attributes,
      };
      this.generatePdf(config, mapPdf, this.fileName);
    },
    calculatePrintScale(extent) {
      const dx = extent[2] - extent[0];
      const dy = extent[3] - extent[1];
      return Math.max((dx * 100) / 20.1, (dy * 100) / 28.7);
    },
  },
};
</script>
